import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button } from '@material-ui/core';

import { productApi as api } from 'api/product';
import { cohortSourceTypeSelectOptions } from 'components/product/utils/cohortDisplay';
import { DataTable, Notifications } from 'shared';
import dayjs from 'utils/dayjsConfig';

/** react-table Cell; reads handlers from column.meta (set in useMemo). */
function CohortBrazeSyncTableCell({ column, row }) {
    const onSync = column?.meta?.onSync;
    const syncingId = column?.meta?.syncingId;
    if (!onSync) {
        return null;
    }
    if (row.original.sourceType !== 'braze_segment') {
        return '—';
    }
    const busy = syncingId === row.original.id;
    return (
        <Button
            color="primary"
            disabled={busy}
            onClick={(e) => {
                e.stopPropagation();
                onSync(row.original.id);
            }}
            size="small"
            variant="outlined"
        >
            {busy ? 'Starting…' : 'Sync now'}
        </Button>
    );
}

function formatSyncAt(value) {
    if (value == null || value === '') {
        return '—';
    }
    const d = dayjs(value);
    return d.isValid() ? d.format('MMM D, YYYY h:mm A') : String(value);
}

function ViewCohort() {
    const [rows, setRows] = useState([]);
    const [notification, setNotification] = useState({ text: null, level: null });
    const [syncingCohortId, setSyncingCohortId] = useState(null);

    const getCohorts = useCallback(async () => {
        const response = await api.getCohorts();
        if (response.status === false) {
            setNotification({
                text: `Could not load cohorts${response.msg ? `: ${response.msg}` : ''}`,
                level: 'error',
            });
            setRows([]);
            return;
        }

        const raw = response.data.rows ?? [];
        setRows(
            raw.map((row) => ({
                ...row,
                sourceType: row.sourceType == null ? '' : row.sourceType,
                sourceRef: row.sourceRef == null ? '' : row.sourceRef,
            })),
        );
    }, []);

    const runBrazeSync = useCallback(
        async (cohortID) => {
            setSyncingCohortId(cohortID);
            try {
                const res = await api.syncCohort(cohortID);
                if (!res.status) {
                    setNotification({ text: res.msg || 'Sync request failed', level: 'error' });
                    return;
                }
                setNotification({ text: 'Braze sync started', level: 'success' });
                getCohorts();
            } finally {
                setSyncingCohortId(null);
            }
        },
        [getCohorts],
    );

    const columns = useMemo(
        () => [
            { accessor: 'id', Header: 'ID', editable: false, width: '72px' },
            { accessor: 'name', Header: 'Name', editable: true },
            { accessor: 'description', Header: 'Description', editable: true },
            {
                accessor: 'sourceType',
                Header: 'Source type',
                editable: true,
                editProps: {
                    type: 'select',
                    options: cohortSourceTypeSelectOptions,
                },
            },
            {
                accessor: 'sourceRef',
                Header: 'Source ref',
                editable: true,
                editProps: { type: 'input', inputType: 'text' },
            },
            {
                id: 'brazeSync',
                Header: 'Braze sync',
                editable: false,
                meta: { onSync: runBrazeSync, syncingId: syncingCohortId },
                Cell: CohortBrazeSyncTableCell,
            },
            {
                accessor: 'lastSyncStartedAt',
                Header: 'Sync started',
                editable: false,
                Cell: ({ cell: { value } }) => formatSyncAt(value),
            },
            {
                accessor: 'lastSyncCompletedAt',
                Header: 'Sync completed',
                editable: false,
                Cell: ({ cell: { value } }) => formatSyncAt(value),
            },
            {
                accessor: 'lastSyncStatus',
                Header: 'Sync status',
                editable: false,
                Cell: ({ cell: { value } }) => (value == null || value === '' ? '—' : String(value)),
            },
            {
                accessor: 'lastSyncError',
                Header: 'Sync error',
                editable: false,
                Cell: ({ cell: { value } }) => (value == null || value === '' ? '—' : String(value)),
            },
        ],
        [runBrazeSync, syncingCohortId],
    );

    useEffect(() => {
        getCohorts();
    }, [getCohorts]);

    return (
        <div>
            <h1>Cohorts</h1>
            <Notifications options={notification} />
            {}
            <div>
                <DataTable
                    columns={columns}
                    data={rows}
                    editable
                    saveEdit={async ({ field, id, value }) => {
                        const payload = { [field]: value };
                        const res = await api.updateCohort(id, payload);
                        if (!res.status) {
                            setNotification({ text: res.msg, level: 'error' });
                            return false;
                        }

                        setNotification({ text: 'Cohort updated', level: 'success' });
                        getCohorts();
                        return true;
                    }}
                />
            </div>
        </div>
    );
}

export { ViewCohort };
